import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import { Box, Drawer, IconButton } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import Iconify from 'src/components/iconify';

// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
// mock
import useResponsive from '../../../hooks/useResponsive';
import NavSection from '../../../components/nav-section';
// services
import authService from '../../../services/auth.service';
//
import {
  adminNavConfig,
  agentNavConfig,
  commonNavConfig,
  customerNavConfig,
  depManagerNavConfig,
} from './config';

const NAV_WIDTH = 280;
const NAV_WIDTH_REDUCED = 80;

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const theme = useTheme();
  const role = authService.verifyUserRole();
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const { isSmallScreen } = useResponsiveScreen();
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (isDesktop && openNav) {
      onCloseNav();
    }
  }, [pathname, openNav, onCloseNav, isDesktop]);

  const toggleSidebar = () => {
    setIsExpanded((prev) => !prev);
  };

  const currentNavWidth = isDesktop
    ? isExpanded
      ? NAV_WIDTH
      : NAV_WIDTH_REDUCED
    : NAV_WIDTH_REDUCED;

  const effectiveExpanded = isDesktop ? isExpanded : false;

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        minWidth: 0,
        boxSizing: 'border-box',
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        },
      }}
    >
      <Box
        sx={{
          px: effectiveExpanded ? 2.5 : 0,
          py: isSmallScreen ? 1 : 3,
          display: 'flex',
          flexDirection: effectiveExpanded ? 'row' : 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 64,
          transition: 'all 0.3s ease',
          boxSizing: 'border-box',
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            mb: effectiveExpanded ? 0 : 1,
            minWidth: 0,
          }}
        >
          <Logo isExpanded={effectiveExpanded} style={{ maxWidth: '100%' }} />
        </Box>
      </Box>

      <Box
        sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}
      >
        {(() => {
          switch (role) {
            case 'ROLE_ADMIN':
              return <NavSection data={adminNavConfig} isExpanded={effectiveExpanded} />;
            case 'ROLE_AGENT':
              return <NavSection data={agentNavConfig} isExpanded={effectiveExpanded} />;
            case 'ROLE_CUSTOMER':
              return (
                <NavSection data={customerNavConfig} isExpanded={effectiveExpanded} />
              );
            case 'ROLE_DEPARTMENTMANAGER':
              return (
                <NavSection data={depManagerNavConfig} isExpanded={effectiveExpanded} />
              );
            default:
              return <NavSection data={commonNavConfig} isExpanded={effectiveExpanded} />;
          }
        })()}
      </Box>

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: currentNavWidth },
        transition: 'width 0.3s ease',
        position: 'relative',
        overflowX: 'hidden', // prevent horizontal scroll
        minWidth: 0,
      }}
    >
      {isDesktop ? (
        <>
          <Drawer
            open
            variant="permanent"
            PaperProps={{
              sx: {
                width: currentNavWidth,
                maxWidth: currentNavWidth,
                boxSizing: 'border-box',
                bgcolor: 'background.default',
                borderRightStyle: 'dashed',
                transition: 'width 0.3s ease',
                overflowX: 'hidden',
                overflowY: 'auto',
              },
            }}
          >
            {renderContent}
          </Drawer>

          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{
              position: 'fixed',
              top: isSmallScreen ? 28 : 60,
              left: currentNavWidth,
              zIndex: 1150,
              p: 0,
              width: 18,
              height: 45,
              backgroundColor: '#919EAB',
              borderRadius: '0 6px 6px 0',
              border: `1px solid '#919EAB'`,
              borderLeft: 'none',
              boxShadow: '2px 0 6px rgba(0,0,0,0.1)',
              color: 'white',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {isExpanded ? (
              <Iconify icon="garden:chevron-left-fill-12" width="80%" height="80%" />
            ) : (
              <Iconify icon="garden:chevron-right-fill-12" width="80%" height="80%" />
            )}
          </IconButton>
        </>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH_REDUCED,
              maxWidth: NAV_WIDTH_REDUCED,
              boxSizing: 'border-box',
              bgcolor: 'background.default',
              overflowX: 'hidden',
              overflowY: 'auto',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
